import { type GameConfig, gameConfig, WORLD_HEIGHT, WORLD_WIDTH } from './config'
import type { Shuttle } from './shuttle'

/**
 * Падающие бонусы — чистые функции, как и остальная игра. state.ts решает,
 * когда их звать; здесь — что с ними происходит.
 */

export type BonusKind =
  | 'coin'
  | 'wide'
  | 'slow'
  | 'double'
  | 'shield'
  | 'narrow'
  | 'fast'
  | 'fog'
  | 'breaker'

export const BONUS_KINDS: readonly BonusKind[] = [
  'coin',
  'wide',
  'slow',
  'double',
  'shield',
  'narrow',
  'fast',
  'fog',
  'breaker',
]

/** Отрицательные бонусы — их надо избегать, а не ловить. */
export const NEGATIVE_KINDS: ReadonlySet<BonusKind> = new Set(['narrow', 'fast', 'fog', 'breaker'])

export function isNegative(kind: BonusKind): boolean {
  return NEGATIVE_KINDS.has(kind)
}

export interface FallingBonus {
  kind: BonusKind
  /** Ось покачивания; фактический x — bonusX(). */
  baseX: number
  y: number
  /** Сколько игровых секунд падает — фаза покачивания. */
  age: number
}

/** Действующие эффекты: сколько реальных секунд осталось, у страховки — есть ли. */
export interface Effects {
  wide: number
  slow: number
  double: number
  shield: boolean
  narrow: number
  fast: number
  fog: number
}

export const NO_EFFECTS: Effects = {
  wide: 0,
  slow: 0,
  double: 0,
  shield: false,
  narrow: 0,
  fast: 0,
  fog: 0,
}

export function bonusX(b: FallingBonus, cfg: GameConfig = gameConfig): number {
  return b.baseX + Math.sin(b.age * cfg.bonus.swayRate) * cfg.bonus.swayAmp
}

/** Вид бонуса по случайному числу [0, 1) с учётом весов. */
export function pickKind(r: number, cfg: GameConfig = gameConfig): BonusKind {
  const weights = cfg.bonus.weights
  const total = BONUS_KINDS.reduce((sum, k) => sum + weights[k], 0)
  let acc = 0
  for (const kind of BONUS_KINDS) {
    acc += weights[kind] / total
    if (r < acc) return kind
  }
  return 'coin'
}

/** После отбоя: может, выпадет бонус. Появляется над верхним краем поля. */
export function maybeSpawn(
  onField: number,
  hits: number,
  rng: () => number,
  cfg: GameConfig = gameConfig,
): FallingBonus | null {
  const { fromHits, chance, maxOnField, swayAmp, radius } = cfg.bonus
  if (hits < fromHits || onField >= maxOnField || rng() >= chance) return null
  const kind = pickKind(rng(), cfg)
  // Покачивание не должно выносить бонус за стенку.
  const margin = swayAmp + radius + 8
  const baseX = margin + rng() * (WORLD_WIDTH - 2 * margin)
  return { kind, baseX, y: -radius, age: 0 }
}

/** Падение; упавшие ниже поля исчезают. */
export function stepBonuses(
  bonuses: FallingBonus[],
  dt: number,
  cfg: GameConfig = gameConfig,
): FallingBonus[] {
  return bonuses
    .map((b) => ({ ...b, y: b.y + cfg.bonus.fallSpeed * dt, age: b.age + dt }))
    .filter((b) => b.y - cfg.bonus.radius <= WORLD_HEIGHT)
}

/** Что волан задел за этот шаг. */
export function catchBonuses(
  bonuses: FallingBonus[],
  shuttle: Shuttle,
  cfg: GameConfig = gameConfig,
): { left: FallingBonus[]; caught: BonusKind[] } {
  const reach = cfg.bonus.radius + cfg.shuttle.radius
  const left: FallingBonus[] = []
  const caught: BonusKind[] = []
  for (const b of bonuses) {
    if (Math.hypot(bonusX(b, cfg) - shuttle.x, b.y - shuttle.y) <= reach) caught.push(b.kind)
    else left.push(b)
  }
  return { left, caught }
}

/**
 * Эффект пойманного бонуса. Повторный — продлевает до полной длительности, а
 * не складывается: иначе два «×2» подряд давали бы полминуты удвоения.
 * Противоположные гасят друг друга (узкая ↔ широкая, ускорение ↔ замедление):
 * иначе одновременно действовали бы оба, и игрок не понял бы, что происходит.
 * Монета и «сбой серии» эффекта не дают — их обрабатывает state.ts.
 */
export function applyBonus(e: Effects, kind: BonusKind, cfg: GameConfig = gameConfig): Effects {
  const b = cfg.bonus
  switch (kind) {
    case 'wide':
      return { ...e, wide: b.wideDuration, narrow: 0 }
    case 'narrow':
      return { ...e, narrow: b.narrowDuration, wide: 0 }
    case 'slow':
      return { ...e, slow: b.slowDuration, fast: 0 }
    case 'fast':
      return { ...e, fast: b.fastDuration, slow: 0 }
    case 'double':
      return { ...e, double: b.doubleDuration }
    case 'shield':
      return { ...e, shield: true }
    case 'fog':
      return { ...e, fog: b.fogDuration }
    case 'breaker':
      // Удвоение — часть «темпа», и сбой снимает его вместе с серией.
      return { ...e, double: 0 }
    case 'coin':
      return e
  }
}

export function tickEffects(e: Effects, dt: number): Effects {
  return {
    wide: Math.max(0, e.wide - dt),
    slow: Math.max(0, e.slow - dt),
    double: Math.max(0, e.double - dt),
    shield: e.shield,
    narrow: Math.max(0, e.narrow - dt),
    fast: Math.max(0, e.fast - dt),
    fog: Math.max(0, e.fog - dt),
  }
}
